import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {useRef, useState} from 'react'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {medicalReportKeyMapToLabel} from '@/features/medical-reports/utils'
import {MedicalReportTypeByProject} from '../../types'
import {useReportTypesSearchableListByPatient} from '../../hooks/useReportTypesSearchableListByPatient'

interface InputSelectReportTypeByPatientProps {
    patientId: string
    userId: string
    disabled?: boolean
    label?: string
    value: MedicalReportTypeByProject | null
    onChange: (value: MedicalReportTypeByProject | null) => void
    placeholder?: string
    errorMessage?: string
    onClear?: () => void
}

export const InputSelectReportTypeByPatient = ({
    patientId,
    userId,
    disabled,
    label,
    onChange,
    errorMessage,
    placeholder,
    onClear,
    value
}: InputSelectReportTypeByPatientProps) => {
    const {t} = useTranslation()
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const {reportTypes, isLoading, isError, onSearch, onResetSearch, hasNextPage, fetchNextPage} =
        useReportTypesSearchableListByPatient({
            patientId,
            userId,
            enabled: !disabled && isQueryEnabled
        })

    return (
        <ComboboxProvider setValue={onSearch} resetValueOnHide>
            <SelectProvider
                value={value?.id ?? ''}
                setValue={value => onChange(reportTypes.find(type => type.id === value) ?? null)}
                setOpen={isOpen => {
                    setIsQueryEnabled(isOpen)
                    onResetSearch(inputRef)
                }}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    {label && <Label>{label}</Label>}
                    <SelectTrigger
                        placeholder={placeholder ?? t('commons:select')}
                        fullWidth
                        value={value?.id}
                        displayValue={() => (
                            <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                                {value?.name}
                                {onClear && (
                                    <XCloseIcon
                                        role="button"
                                        onClick={e => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            onClear()
                                        }}
                                    />
                                )}
                            </Flexbox>
                        )}
                        hasError={!!errorMessage}
                        disabled={disabled}
                    />

                    {errorMessage && (
                        <Flexbox align="center" gap={1.5}>
                            <InputStatusIcon hasError={!!errorMessage} />
                            <InputHelpText error={errorMessage} />
                        </Flexbox>
                    )}

                    <SelectComboboxPopover sameWidth>
                        <SelectComboboxInput ref={inputRef} placeholder={t('commons:search_placeholder')} />
                        <SelectComboboxList>
                            {isLoading ? (
                                Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
                            ) : isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : reportTypes.length ? (
                                <SelectVirtualList
                                    data={reportTypes}
                                    totalCount={reportTypes.length}
                                    overscan={100}
                                    endReached={() => hasNextPage && fetchNextPage()}
                                    itemContent={(itemIndex: number, item: MedicalReportTypeByProject) => (
                                        <SelectItem hasItemCheckbox key={itemIndex} value={item.id}>
                                            {t(medicalReportKeyMapToLabel[item.keyName])}
                                        </SelectItem>
                                    )}
                                />
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
